import uuid from 'uuid/v4';

import { getSceneStartEndTimes } from 'js/playback/helpers/timings';
import { NewAudioLayerType } from 'js/editor/Timeline/AdvancedTimeline/hooks/useLayoutTopToAudioLayer';
import { ScribeAudioLayerModel } from 'js/models/ScribeAudioLayerModel';
import { AudioClip, ExistingScribeModel, ScribeScene } from 'js/types';

function removeLayer(layers: ScribeAudioLayerModel[], layerId: string) {
  return layers.filter(layer => layer.id !== layerId);
}

function removeLayerId(layerIds: string[] | undefined, layerId: string) {
  if (!layerIds) return;
  return layerIds.filter(id => id !== layerId);
}

function removeAudioLayerFromSceneAndProject(project: ExistingScribeModel, activeScene: ScribeScene, layerId: string) {
  project.audioLayers = removeLayer(project.audioLayers, layerId);
  project.projectAudioLayerIds = removeLayerId(project.projectAudioLayerIds, layerId);
  activeScene.audioLayerIds = removeLayerId(activeScene.audioLayerIds, layerId);
}

export function updateAudioLayers(
  project: ExistingScribeModel,
  audioClip: AudioClip,
  newLayerId: string | NewAudioLayerType
) {
  const existingLayer = project.audioLayers.find(layer => layer.audioClipIds.includes(audioClip.id));
  const activeScene = project.scenes.find(scene => scene.active);
  const existingAudioType = audioClip.type;

  if (existingLayer?.id === newLayerId || !activeScene) return;

  const sceneStartTime = getSceneStartEndTimes(project, activeScene).startTime / 1000;

  if (newLayerId === NewAudioLayerType.ProjectLayer) {
    const newLayer = new ScribeAudioLayerModel({
      id: uuid(),
      audioClipIds: [audioClip.id]
    });

    project.audioLayers.push(newLayer);
    audioClip.type = 'project';

    if (existingAudioType !== 'project') {
      // Clip is moving from scene to project, adjust start time to include scene start time
      audioClip.startTime = (audioClip.startTime ?? 0) + sceneStartTime;
    }

    if (existingLayer) {
      existingLayer.audioClipIds = existingLayer.audioClipIds.filter(id => id !== audioClip.id);

      if (existingLayer.audioClipIds.length === 0) {
        removeAudioLayerFromSceneAndProject(project, activeScene, existingLayer.id);
      }
    }

    if (project.projectAudioLayerIds) {
      project.projectAudioLayerIds.push(newLayer.id);
    } else {
      project.projectAudioLayerIds = [newLayer.id];
    }
  } else if (newLayerId === NewAudioLayerType.ActiveSceneLayer) {
    const newLayer = new ScribeAudioLayerModel({
      id: uuid(),
      audioClipIds: [audioClip.id]
    });

    project.audioLayers.push(newLayer);
    audioClip.type = 'scene';

    if (existingAudioType !== 'scene') {
      // Clip is moving from project to scene, adjust start time to exclude scene start time
      audioClip.startTime = Math.max((audioClip.startTime ?? 0) - sceneStartTime, 0);
    }

    if (existingLayer) {
      existingLayer.audioClipIds = existingLayer.audioClipIds.filter(id => id !== audioClip.id);

      if (existingLayer.audioClipIds.length === 0) {
        removeAudioLayerFromSceneAndProject(project, activeScene, existingLayer.id);
      }
    }

    if (activeScene.audioLayerIds) {
      activeScene.audioLayerIds.push(newLayer.id);
    } else {
      activeScene.audioLayerIds = [newLayer.id];
    }
  } else {
    const targetLayer = project.audioLayers.find(layer => layer.id === newLayerId);
    if (!targetLayer || !existingLayer) return;

    const clipType =
      targetLayer.id === project.projectAudioLayerIds?.find(id => id === newLayerId) ? 'project' : 'scene';
    audioClip.type = clipType;

    if (clipType !== existingAudioType) {
      // Clip is moving from project to scene or vice versa, adjust start time to include/exclude scene start time
      if (clipType === 'scene') {
        // Adjust start time to include scene start time
        audioClip.startTime = Math.max((audioClip.startTime ?? 0) - sceneStartTime, 0);
      } else {
        // Adjust start time to exclude scene start time
        audioClip.startTime = (audioClip.startTime ?? 0) + sceneStartTime;
      }
    }

    targetLayer.audioClipIds.push(audioClip.id);
    existingLayer.audioClipIds = existingLayer.audioClipIds.filter(id => id !== audioClip.id);

    if (existingLayer.audioClipIds.length === 0) {
      removeAudioLayerFromSceneAndProject(project, activeScene, existingLayer.id);
    }
  }
}
